// 오늘의 브리핑 V2-P0 — deterministic evidence layer (2026-09-17). LLM 없음.
// NovaDB 여러 도메인 데이터를 종합해 source-of-truth evidence object 를 만든다.
// P0/P1A(briefingInsight.js)의 same-time 계산·signals 를 그대로 재사용한다.
//
// 원칙:
//  · 계산 가능한 사실만. 인과 단정 금지 — relationship 은 "동시 관찰 / 확인 필요"만 표현.
//  · sampleSize=low 면 relationship/action 을 과장하지 않고 사실 요약은 계속 제공.
//  · 이 모듈은 순수(React 밖). Infinity/NaN 금지.
import { buildBriefingInsight, changePct, BRIEFING_THRESHOLDS } from "@/lib/briefingInsight";

const DEEP_LINKS = {
  finops: "/dashboard?tab=finops",
  trend: "/dashboard?tab=trend",
  usage: "/dashboard?tab=toolUsage",
  retention: "/dashboard?tab=retention",
  mystyle: "/dashboard?tab=mystyle",
  tickets: "/tickets",
  system: "/system",
};

function num(v) { return typeof v === "number" && Number.isFinite(v) ? v : null; }

// sampleSize — 오늘(부분일) 호출이 적으면 low. same-time 7일평균 floor 도 함께 고려.
function computeSampleSize(insight) {
  const st = insight.sameTime;
  const todayReq = st?.requests?.today;
  const avg7d = st?.requests?.avg7d;
  const floor = BRIEFING_THRESHOLDS.noiseFloor.requestsAvg7d;
  if (todayReq == null) return "unknown";
  if (todayReq < floor || (avg7d != null && avg7d < floor)) return "low";
  return "normal";
}

// 도구 편중 — 상위 도구 점유율. tools = byTool.tools [{tool, engine, requestCount}]
function toolConcentration(tools) {
  const byTool = {};
  let total = 0;
  for (const t of tools || []) {
    const k = t.tool || "unknown";
    const c = num(t.requestCount) || 0;
    byTool[k] = (byTool[k] || 0) + c;
    total += c;
  }
  const rows = Object.entries(byTool).map(([tool, requests]) => ({
    tool, requests, sharePct: total > 0 ? (requests / total) * 100 : null,
  })).sort((a, b) => b.requests - a.requests);
  return { rows, total };
}

// 엔진별 원가 비중 — models = byModel.models [{model/engine, usdCost, requestCount}]
function engineCostShares(models) {
  const rows = [];
  let totalUsd = 0;
  for (const m of models || []) {
    const usd = num(m.usdCost) || 0;
    totalUsd += usd;
    rows.push({ engine: m.model || m.engine || "unknown", usdCost: usd, requests: num(m.requestCount) || 0 });
  }
  return rows.map((r) => ({ ...r, costSharePct: totalUsd > 0 ? (r.usdCost / totalUsd) * 100 : null }))
             .sort((a, b) => b.usdCost - a.usdCost);
}

// ── 메인: evidence 조립 ──────────────────────────────────────────────────────
// 입력(모두 optional, allSettled 로 채운다): overviewKpi, hourlySeries, nowKstHour, todayDate,
//   tools(byTool.tools), models(byModel.models), tickets(cx), retention, devopsHealth, mystyle
export function buildBriefingEvidence(inp = {}) {
  const {
    overviewKpi = {}, hourlySeries = null, nowKstHour, todayDate,
    tools = [], models = [], openTicketsToday = null, newTicketsToday = null,
    severityBreakdown = null, vocCount = null, retention = null,
    devopsHealth = null, mystyle = null, signupTrend = null, newUsersToday = null,
    generatedAt = new Date().toISOString(),
  } = inp;

  // P0/P1A insight(same-time signals + summary) 재사용 = evidence 의 핵심.
  const insight = buildBriefingInsight({
    today: overviewKpi, yesterday: null, sevenDaySeries: null,
    hourlySeries, nowKstHour, todayDate,
    openTicketsToday, openTicketsYesterday: null,
  });

  const sampleSize = computeSampleSize(insight);
  const conc = toolConcentration(tools);
  const engineShares = engineCostShares(models);
  const st = insight.sameTime;

  // dataStatus — 조회 성공 여부와 오늘 활동 유무를 명시적으로 구분(backend parity).
  //   unavailable  : 소스가 사실상 비어 판단 불가(hourly 없음 + overview KPI 없음).
  //   zero_activity: 조회는 됐고 오늘 활동(요청)이 0. 장애 아님.
  //   ok           : 조회 + 활동 있음.
  const reqToday = num(overviewKpi.requestCount);
  const sourcesEmpty = (hourlySeries == null && (overviewKpi == null || Object.keys(overviewKpi).length === 0));
  const dataStatus = sourcesEmpty ? "unavailable" : ((reqToday || 0) === 0 ? "zero_activity" : "ok");

  const evidence = {
    meta: {
      date: todayDate || null,
      cutoffHour: st ? st.cutoffHour : null,
      sampleSize,
      dataStatus,
      generatedAt,
    },
    business: {
      revenueKrw: num(overviewKpi.revenueKrw),
      aiCostKrw: num(overviewKpi.bedrockCostKrw),
      marginKrw: num(overviewKpi.businessMarginKrw),
      marginPct: num(overviewKpi.businessMarginPct),
    },
    usage: {
      requests: num(overviewKpi.requestCount),
      activeUsers: num(overviewKpi.uniqueUsers),
      inputTokens: num(overviewKpi.inputTokens),
      outputTokens: num(overviewKpi.outputTokens),
      // same-time 비교값(정본) — 부분일 vs 완결일 오판 없는 값
      sameTime: st ? { requests: st.requests, activeUsers: st.activeUsers, usdCost: st.usdCost, cutoffHour: st.cutoffHour } : null,
    },
    users: {
      newUsersToday: num(newUsersToday),
      signupTrend: Array.isArray(signupTrend) ? signupTrend.slice(-7) : null,
      retention: retention || null,
    },
    tools: conc.rows.slice(0, 8),
    engines: engineShares.slice(0, 8),
    quality: {
      openTickets: num(openTicketsToday),
      newTicketsToday: num(newTicketsToday),
      severityBreakdown: severityBreakdown || null,
      voc: num(vocCount),
    },
    system: devopsHealth ? {
      errorRatePct: num(devopsHealth.errorRatePct),
      p95LatencyMs: num(devopsHealth.p95LatencyMs),
      deployStatus: devopsHealth.deployStatus ?? null,
      pipelineStatus: devopsHealth.pipelineStatus ?? null,
    } : null,
    mystyle: mystyle ? {
      adoptionRatePct: num(mystyle.adoptionRatePct),
      applySuccessRatePct: num(mystyle.applySuccessRatePct),
      intentMismatch: num(mystyle.intentMismatch),
    } : null,
    signals: insight.signals,
    relationships: [],
  };

  evidence.relationships = buildRelationships(evidence, insight);
  const { pmActions, devActions } = buildActions(evidence);
  evidence.pmActions = pmActions;
  evidence.devActions = devActions;
  evidence.headline = insight.headline; // deterministic fallback 요약
  return evidence;
}

const T = BRIEFING_THRESHOLDS;

// cross-domain relationship — 상관(동시 관찰)만. 인과 단정 금지. low sample 이면 과장 억제.
function buildRelationships(ev, insight) {
  const rels = [];
  const st = insight.sameTime;
  const low = ev.meta.sampleSize === "low";
  const conf = low ? "low" : "medium";

  // usage_up_revenue_flat — same-time 요청 증가 + 매출 정체(오늘 매출 변화 작음)
  if (st && st.requests.avg7d != null) {
    const reqPct = changePct(st.requests.today, st.requests.avg7d);
    // 매출은 same-time 어제 대비(revenue same-time 존재)
    const revPct = changePct(st.revenueKrw?.today, st.revenueKrw?.yesterday);
    if (!low && reqPct != null && reqPct >= T.requestDeviationPct && revPct != null && Math.abs(revPct) < 10) {
      rels.push({
        type: "usage_up_revenue_flat", severity: "info", confidence: conf,
        evidence: [`요청 7일 동시간 평균 대비 ${Math.round(reqPct)}%`, `매출 어제 동시간 대비 ${Math.round(revPct)}%`],
        suggestedCheck: "사용 증가가 매출 전환으로 이어지는지 확인이 필요합니다.", deepLink: DEEP_LINKS.trend,
      });
    }
  }

  // usage_down_tickets_up — 요청 감소 + 오류 제보 존재
  if (st && st.requests.avg7d != null) {
    const reqPct = changePct(st.requests.today, st.requests.avg7d);
    if (!low && reqPct != null && reqPct <= -T.requestDeviationPct && (ev.quality.openTickets || 0) > 0) {
      rels.push({
        type: "usage_down_tickets_up", severity: "warning", confidence: conf,
        evidence: [`요청 7일 동시간 평균 대비 ${Math.round(reqPct)}%`, `미처리 오류 제보 ${ev.quality.openTickets}건`],
        suggestedCheck: "사용 감소와 품질 이슈가 함께 관찰됩니다. 함께 점검이 필요합니다.", deepLink: DEEP_LINKS.tickets,
      });
    }
  }

  // engine_usage_up_cost_up — 특정 엔진 원가 비중 급증
  {
    const top = ev.engines[0];
    if (!low && top && top.costSharePct != null && top.costSharePct >= 50) {
      rels.push({
        type: "engine_usage_up_cost_up", severity: "info", confidence: conf,
        evidence: [`${top.engine} 원가 비중 ${Math.round(top.costSharePct)}%`],
        suggestedCheck: "원가가 특정 엔진에 집중돼 있습니다. 라우팅·단가 확인이 필요합니다.", deepLink: DEEP_LINKS.finops,
      });
    }
  }

  // signup_up_retention_low — 신규 유입 있음 + retention 낮음(가능 시)
  if (!low && ev.users.newUsersToday != null && ev.users.newUsersToday > 0 && ev.users.retention) {
    rels.push({
      type: "signup_up_retention_low", severity: "info", confidence: "low",
      evidence: [`오늘 신규 ${ev.users.newUsersToday}명`],
      suggestedCheck: "유입 대비 재사용 전환을 확인해볼 수 있습니다.", deepLink: DEEP_LINKS.retention,
    });
  }

  // tool_concentration — 상위 도구 편중
  {
    const top = ev.tools[0];
    if (!low && top && top.sharePct != null && top.sharePct >= 60) {
      rels.push({
        type: "tool_concentration", severity: "info", confidence: conf,
        evidence: [`${top.tool} 사용 비중 ${Math.round(top.sharePct)}%`],
        suggestedCheck: "사용이 소수 도구에 집중돼 있습니다. 기능 사용 편중을 확인해볼 수 있습니다.", deepLink: DEEP_LINKS.usage,
      });
    }
  }

  // usage_down_no_quality_issue — 요청 감소 + 오류 없음
  if (st && st.requests.avg7d != null) {
    const reqPct = changePct(st.requests.today, st.requests.avg7d);
    if (!low && reqPct != null && reqPct <= -T.requestDeviationPct && (ev.quality.openTickets || 0) === 0) {
      rels.push({
        type: "usage_down_no_quality_issue", severity: "info", confidence: conf,
        evidence: [`요청 7일 동시간 평균 대비 ${Math.round(reqPct)}%`, "미처리 오류 0건"],
        suggestedCheck: "장애 신호 없이 사용이 감소했습니다. 유입·사용성 측면 확인이 필요할 수 있습니다.", deepLink: DEEP_LINKS.usage,
      });
    }
  }

  // system_error_up — errorRatePct 높음
  if (ev.system && ev.system.errorRatePct != null && ev.system.errorRatePct >= 1) {
    rels.push({
      type: "system_error_up", severity: "warning", confidence: "medium",
      evidence: [`시스템 오류율 ${ev.system.errorRatePct}%`],
      suggestedCheck: "시스템 오류율이 관찰됩니다. 함수 상태를 확인해볼 수 있습니다.", deepLink: DEEP_LINKS.system,
    });
  }

  // mystyle_mismatch_up — intentMismatch 존재
  if (ev.mystyle && ev.mystyle.intentMismatch != null && ev.mystyle.intentMismatch > 0) {
    rels.push({
      type: "mystyle_mismatch_up", severity: "info", confidence: "medium",
      evidence: [`MyStyle intent mismatch ${ev.mystyle.intentMismatch}건`],
      suggestedCheck: "UI와 backend 의도 불일치가 관찰됩니다. 확인이 필요합니다.", deepLink: DEEP_LINKS.mystyle,
    });
  }

  return rels;
}

// PM/Developer action — relationship + signal 기반. 각 최대 3, 같은 deepLink 중복 제거.
function buildActions(ev) {
  const pm = [];
  const dev = [];
  const PM_TYPES = new Set(["usage_up_revenue_flat", "signup_up_retention_low", "tool_concentration", "usage_down_no_quality_issue"]);
  const DEV_TYPES = new Set(["engine_usage_up_cost_up", "system_error_up", "mystyle_mismatch_up", "usage_down_tickets_up"]);

  const pushUniq = (arr, item) => {
    if (arr.some((a) => a.deepLink === item.deepLink)) return;
    if (arr.length >= 3) return;
    arr.push({ ...item, priority: arr.length + 1 });
  };

  for (const r of ev.relationships) {
    const item = { text: r.suggestedCheck, deepLink: r.deepLink, evidence: r.evidence, severity: r.severity };
    if (PM_TYPES.has(r.type)) pushUniq(pm, item);
    else if (DEV_TYPES.has(r.type)) pushUniq(dev, item);
  }
  // signal 기반 보강(개발/운영): margin_negative → PM+Dev 공통 finops, tickets → dev
  for (const s of ev.signals) {
    if (s.type === "margin_negative") pushUniq(pm, { text: "기여이익 적자 — 매출·원가 구성 확인", deepLink: DEEP_LINKS.finops, evidence: ["기여이익 < 0"], severity: "critical" });
    if (s.type === "tickets_open") pushUniq(dev, { text: `미처리 오류 제보 ${ev.quality.openTickets}건 확인`, deepLink: DEEP_LINKS.tickets, evidence: [`open ${ev.quality.openTickets}`], severity: s.severity });
    if (s.type === "cost_spike") pushUniq(dev, { text: "AI 원가 급증 — 엔진 라우팅 확인", deepLink: DEEP_LINKS.finops, evidence: s ? [s.detail] : [], severity: "warning" });
  }
  return { pmActions: pm, devActions: dev };
}
